import axios from "axios";
import Vue from "vue";
import { BASEURL } from "@/config/index";
import { applyPrintSettingsToHttpUrl } from "@/helper/posPrintSettings";

const ALLOWED_JWT_ALGS = ["HS256", "HS384", "HS512", "RS256", "RS384", "RS512"];
let invalidJwtLogged = false;
let invalidBaseUrlLogged = false;

function decodeJwtSegment(segment) {
  if (typeof segment !== "string" || !segment) return null;
  try {
    const padded =
      segment.replace(/-/g, "+").replace(/_/g, "/") +
      "===".slice((segment.length + 3) % 4);
    const decoded = JSON.parse(atob(padded));
    if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) {
      return null;
    }
    return decoded;
  } catch (err) {
    return null;
  }
}

function normalizeJwtToken(token) {
  if (typeof token !== "string") return "";
  return token.trim().replace(/^Bearer\s+/i, "");
}

function isValidJwtToken(token) {
  const value = normalizeJwtToken(token);
  if (!value || value.length > 4096) return false;
  if (/\s/.test(value)) return false;
  const parts = value.split(".");
  if (parts.length !== 3) return false;
  if (!parts.every((part) => /^[A-Za-z0-9_-]+$/.test(part))) return false;
  const header = decodeJwtSegment(parts[0]);
  const payload = decodeJwtSegment(parts[1]);
  if (!header || !payload) return false;
  const alg = typeof header.alg === "string" ? header.alg.toUpperCase() : "";
  if (ALLOWED_JWT_ALGS.indexOf(alg) === -1) return false;
  if (header.typ && String(header.typ).toUpperCase() !== "JWT") return false;
  if (payload.exp != null && typeof payload.exp !== "number") return false;
  return true;
}

function sanitizeJwtForAuthHeader(token) {
  const value = normalizeJwtToken(token);
  if (!isValidJwtToken(value)) return "";
  return value;
}

function discardInvalidStoredJwt(context) {
  const ctx = context || {};
  try {
    const stored = window.localStorage.getItem("JWT_TOKEN");
    if (!stored) {
      console.warn("JWT_TOKEN discard requested but no stored token", {
        source: ctx.source || "unknown",
        reason: ctx.reason || "unknown",
      });
      return;
    }
    if (isValidJwtToken(stored)) {
      console.warn("JWT_TOKEN discard skipped, stored token is valid", {
        source: ctx.source || "unknown",
        reason: ctx.reason || "unknown",
      });
      return;
    }
    window.localStorage.removeItem("JWT_TOKEN");
    console.error("JWT_TOKEN discarded", {
      source: ctx.source || "unknown",
      reason: ctx.reason || describeJwtProblem(stored),
      method: ctx.method,
      url: ctx.url,
      tokenLength: normalizeJwtToken(stored).length,
      at: new Date().toISOString(),
    });
    if (!invalidJwtLogged) {
      invalidJwtLogged = true;
      try {
        if (Vue.toasted && typeof Vue.toasted.show === "function") {
          Vue.toasted.show("Session invalid. Please login again.");
        }
      } catch (toastErr) {
        console.error("JWT discard toast failed", toastErr);
      }
    }
  } catch (err) {
    console.error("unable to discard invalid JWT_TOKEN", {
      source: ctx.source || "unknown",
      error: err && err.message ? err.message : String(err),
    });
  }
}

function describeJwtProblem(token) {
  const value = normalizeJwtToken(token);
  if (!value) return "empty";
  if (value.length > 4096) return "too_long";
  if (/\s/.test(value)) return "contains_whitespace";
  const parts = value.split(".");
  if (parts.length !== 3) return "wrong_segment_count";
  if (!parts.every((part) => /^[A-Za-z0-9_-]+$/.test(part))) {
    return "invalid_base64url";
  }
  const header = decodeJwtSegment(parts[0]);
  if (!header) return "undecodable_header";
  const payload = decodeJwtSegment(parts[1]);
  if (!payload) return "undecodable_payload";
  const alg = typeof header.alg === "string" ? header.alg.toUpperCase() : "";
  if (ALLOWED_JWT_ALGS.indexOf(alg) === -1) return "disallowed_alg";
  if (header.typ && String(header.typ).toUpperCase() !== "JWT") {
    return "wrong_typ";
  }
  if (payload.exp != null && typeof payload.exp !== "number") {
    return "invalid_exp";
  }
  return "unknown";
}

function stripInvalidAuthHeader(headers, requestContext) {
  if (!headers || typeof headers !== "object") return;
  const authHeader =
    headers.Authorization ||
    headers.authorization ||
    (headers.common &&
      (headers.common.Authorization || headers.common.authorization));
  const sanitized = sanitizeJwtForAuthHeader(authHeader);
  if (authHeader && !sanitized) {
    const reason = describeJwtProblem(authHeader);
    console.warn("Authorization header rejected and removed", {
      reason,
      method: requestContext && requestContext.method,
      url: requestContext && requestContext.url,
      tokenLength: normalizeJwtToken(authHeader).length,
    });
    discardInvalidStoredJwt({
      source: "request_interceptor",
      reason,
      method: requestContext && requestContext.method,
      url: requestContext && requestContext.url,
    });
    if (headers.Authorization) delete headers.Authorization;
    if (headers.authorization) delete headers.authorization;
    if (headers.common) {
      delete headers.common.Authorization;
      delete headers.common.authorization;
    }
    return;
  }
  if (sanitized) {
    if (headers.Authorization) headers.Authorization = sanitized;
    if (headers.authorization) headers.authorization = sanitized;
    if (headers.common) {
      if (headers.common.Authorization) {
        headers.common.Authorization = sanitized;
      }
      if (headers.common.authorization) {
        headers.common.authorization = sanitized;
      }
    }
  }
}

function isPrivateHostname(host) {
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host === "::1"
  ) {
    return true;
  }
  const match = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!match) return false;
  const octets = match.slice(1).map(Number);
  if (octets.some((n) => n > 255)) return false;
  const a = octets[0];
  const b = octets[1];
  if (a === 10 || a === 127) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
}

const TRUSTED_API_HOSTS = [
  "novocinemas.com",
  "nuviotech.co",
  "starcinemas.ae",
  "tonomous.io",
];

function isTrustedHostname(host) {
  if (isPrivateHostname(host)) return true;
  return TRUSTED_API_HOSTS.some(
    (trusted) => host === trusted || host.endsWith("." + trusted)
  );
}

function resolveTrustedBaseUrl(value) {
  if (typeof value !== "string") return "";
  const raw = value.trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    if (parsed.username || parsed.password) return "";
    const host = String(parsed.hostname || "").toLowerCase();
    if (!host || !isTrustedHostname(host)) return "";
    if (parsed.protocol === "http:" && !isPrivateHostname(host)) return "";
    return raw;
  } catch (err) {
    return "";
  }
}

export default () => {
  const trustedBaseUrl = resolveTrustedBaseUrl(BASEURL);
  if (BASEURL && !trustedBaseUrl && !invalidBaseUrlLogged) {
    invalidBaseUrlLogged = true;
    console.error("Invalid BASEURL ignored");
  }
  const instance = axios.create({
    baseURL: trustedBaseUrl || undefined,
    timeout: 60000,
  });
  let JWT = "";
  try {
    JWT = window.localStorage.getItem("JWT_TOKEN");
  } catch (err) {
    JWT = "";
  }
  const sanitizedJwt = sanitizeJwtForAuthHeader(JWT);
  if (JWT && !sanitizedJwt) {
    const reason = describeJwtProblem(JWT);
    console.warn("Stored JWT_TOKEN rejected", {
      reason,
      tokenLength: normalizeJwtToken(JWT).length,
    });
    discardInvalidStoredJwt({ source: "api_client_init", reason });
  } else if (sanitizedJwt) {
    instance.defaults.headers.common["Authorization"] = sanitizedJwt;
  }
  instance.interceptors.request.use(
    function (config) {
      try {
        stripInvalidAuthHeader(config && config.headers, {
          method: config && config.method,
          url: config && config.url,
        });
        if (!config || typeof config.url !== "string") {
          return config;
        }
        const method = String(config.method || "get").toLowerCase();
        if (method !== "get") {
          return config;
        }
        const originalUrl = config.url;
        const nextUrl = applyPrintSettingsToHttpUrl(originalUrl);
        if (typeof nextUrl === "string" && nextUrl) {
          config.url = nextUrl;
        } else {
          config.url = originalUrl;
        }
      } catch (err) {
        console.error("print settings url interceptor", err);
      }
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );
  instance.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error && error.response && error.response.status == 403) {
        Vue.toasted.show("You are not authorized");
        return Promise.reject(error);
      }
      if (error && error.response && error.response.status == 401) {
        try {
          localStorage.clear();
          Vue.cookie.delete("BRHSID");
        } catch (err) {
          console.error("session cleanup failed", err);
        }
        Vue.toasted.show("Session expired. Please login again.");
        if (typeof vmVue !== "undefined" && vmVue && vmVue.$router) {
          vmVue.$router.push({
            path: "/",
          });
        }
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
  return instance;
};



// WEBPACK FOOTER //
// ./src/services/Api.js